/**
 * MembershipFormModal — create (and edit stub) for memberships.
 *
 * Student picker: autocomplete search on active students.
 * Package picker: lists active packages.
 * Amount: defaults from package price, overridable.
 * Recurrence: preset dropdown + custom mode.
 * First invoice date: date picker, defaults to today.
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { Button, Input, Modal, Dropdown } from '@/components/common';
import { useCreateMembership } from '@features/memberships/api/hooks';
import { useStudents } from '@features/people/api/students';
import { usePackages } from '@features/catalog/api/packages';
import { formatPaise, paiseToRupeeInput, rupeeInputToPaise } from '@features/catalog/config/ui';
import { RECURRENCE_PRESETS } from '@features/memberships/config/ui';
import type { RecurrencePattern } from '@features/memberships/api/client';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ErrorMsg = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.error};
  margin: 2px 0 0;
`;

const RecurrenceRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

const SearchResults = styled.ul`
  list-style: none;
  padding: 0;
  margin: 4px 0 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  max-height: 200px;
  overflow-y: auto;
  position: absolute;
  width: 100%;
  z-index: 10;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const SearchResultItem = styled.li`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }
`;

const Relative = styled.div`
  position: relative;
`;

const SelectedPill = styled.div`
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
`;

const today = () => new Date().toISOString().slice(0, 10);

type RecurrencePresetKey = 'monthly' | 'bimonthly' | 'quarterly' | 'half-yearly' | 'yearly' | 'custom';

const PRESET_KEYS: { key: RecurrencePresetKey; label: string }[] = [
  { key: 'monthly', label: 'Monthly' },
  { key: 'bimonthly', label: 'Bi-monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'half-yearly', label: 'Half-yearly' },
  { key: 'yearly', label: 'Yearly' },
  { key: 'custom', label: 'Custom…' },
];

function presetToRecurrence(key: RecurrencePresetKey): RecurrencePattern | null {
  const found = RECURRENCE_PRESETS.find((p) => p.label.toLowerCase() === key.replace(/-/g, ''));
  if (!found) {
    // fallback map
    switch (key) {
      case 'monthly':     return { unit: 'month', interval: 1 };
      case 'bimonthly':   return { unit: 'month', interval: 2 };
      case 'quarterly':   return { unit: 'month', interval: 3 };
      case 'half-yearly': return { unit: 'month', interval: 6 };
      case 'yearly':      return { unit: 'year',  interval: 1 };
      default:            return null;
    }
  }
  return { unit: found.unit as RecurrencePattern['unit'], interval: found.interval };
}

interface FormState {
  studentId: string;
  studentSearch: string;
  packageId: string;
  amount: string;
  recurrencePreset: RecurrencePresetKey;
  customUnit: RecurrencePattern['unit'];
  customInterval: string;
  customDayOfMonth: string;
  customMonthOfYear: string;
  firstInvoiceDate: string;
  currency: string;
}

const EMPTY_FORM: FormState = {
  studentId: '',
  studentSearch: '',
  packageId: '',
  amount: '',
  recurrencePreset: 'monthly',
  customUnit: 'month',
  customInterval: '1',
  customDayOfMonth: '',
  customMonthOfYear: '',
  firstInvoiceDate: today(),
  currency: 'INR',
};

interface FormErrors {
  studentId?: string;
  packageId?: string;
  amount?: string;
  recurrence?: string;
  firstInvoiceDate?: string;
}

export const MembershipFormModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showStudentResults, setShowStudentResults] = useState(false);

  const createMut = useCreateMembership();

  const { data: studentsData } = useStudents({
    status: 'active',
    search: form.studentSearch,
    limit: 20,
  });
  const { data: packagesData } = usePackages({ status: 'active' });

  // Flatten paginated students
  const students =
    studentsData?.pages.flatMap((p) => p.items) ?? [];
  const packages = packagesData?.items ?? [];

  // When package changes, default amount to package price
  useEffect(() => {
    if (!form.packageId) return;
    const pkg = packages.find((p) => p.id === form.packageId);
    if (pkg && form.amount === '') {
      setForm((f) => ({ ...f, amount: paiseToRupeeInput(pkg.price) }));
    }
  }, [form.packageId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_FORM);
      setErrors({});
      setShowStudentResults(false);
    }
  }, [isOpen]);

  const selectStudent = (id: string, name: string) => {
    setForm((f) => ({ ...f, studentId: id, studentSearch: name }));
    setShowStudentResults(false);
    setErrors((e) => ({ ...e, studentId: undefined }));
  };

  const buildRecurrence = (): RecurrencePattern | null => {
    if (form.recurrencePreset !== 'custom') {
      return presetToRecurrence(form.recurrencePreset);
    }
    const interval = parseInt(form.customInterval, 10);
    if (!interval || interval < 1) return null;
    const rec: RecurrencePattern = {
      unit: form.customUnit,
      interval,
    };
    if (form.customDayOfMonth) {
      rec.day_of_month = parseInt(form.customDayOfMonth, 10);
    }
    if (form.customMonthOfYear && form.customUnit === 'year') {
      rec.month_of_year = parseInt(form.customMonthOfYear, 10);
    }
    return rec;
  };

  const validate = (): boolean => {
    const errs: FormErrors = {};
    if (!form.studentId) errs.studentId = 'Student is required';
    if (!form.packageId) errs.packageId = 'Package is required';
    const amt = Number(String(form.amount).replace(/[,\s₹]/g, ''));
    if (!Number.isFinite(amt) || amt < 0) errs.amount = 'Enter a valid amount (₹)';
    if (!buildRecurrence()) errs.recurrence = 'Invalid recurrence';
    if (!form.firstInvoiceDate) errs.firstInvoiceDate = 'Date is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const recurrence = buildRecurrence();
    if (!recurrence) {
      toast.error('Invalid recurrence pattern');
      return;
    }
    const result = await createMut.mutateAsync({
      student_id: form.studentId,
      package_id: form.packageId,
      amount: rupeeInputToPaise(form.amount),
      currency: form.currency,
      recurrence,
      first_invoice_date: form.firstInvoiceDate,
    });
    onClose();
    navigate(`/admin/memberships/${result.id}`);
  };

  const selectedPackage = packages.find((p) => p.id === form.packageId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create membership"
      size="lg"
      data-testid="candeza-memberships-form-modal"
    >
      <FormGrid>
        {/* Student picker */}
        <FullWidth>
          <label
            style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}
          >
            Student *
          </label>
          <Relative data-testid="candeza-memberships-form-student-picker">
            <Input
              placeholder="Search student…"
              value={form.studentSearch}
              onChange={(e) => {
                setForm((f) => ({ ...f, studentSearch: e.target.value, studentId: '' }));
                setShowStudentResults(true);
              }}
              onFocus={() => setShowStudentResults(true)}
              onBlur={() => setTimeout(() => setShowStudentResults(false), 200)}
            />
            {showStudentResults && form.studentSearch.length >= 1 && (
              <SearchResults>
                {students.length === 0 ? (
                  <SearchResultItem style={{ color: '#64748b' }}>
                    No students found
                  </SearchResultItem>
                ) : (
                  students.slice(0, 10).map((s) => (
                    <SearchResultItem
                      key={s.id}
                      onMouseDown={() =>
                        selectStudent(
                          s.id,
                          `${s.first_name} ${s.last_name}`.trim(),
                        )
                      }
                    >
                      {s.first_name} {s.last_name}
                    </SearchResultItem>
                  ))
                )}
              </SearchResults>
            )}
            {form.studentId && (
              <SelectedPill>
                ✓ {form.studentSearch}
                <button
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  onClick={() =>
                    setForm((f) => ({ ...f, studentId: '', studentSearch: '' }))
                  }
                >
                  ×
                </button>
              </SelectedPill>
            )}
          </Relative>
          {errors.studentId && <ErrorMsg>{errors.studentId}</ErrorMsg>}
        </FullWidth>

        {/* Package picker */}
        <FullWidth data-testid="candeza-memberships-form-package-picker">
          <Dropdown
            label="Package *"
            placeholder="Select package"
            value={form.packageId}
            onChange={(v) => {
              setForm((f) => ({ ...f, packageId: v, amount: '' }));
              setErrors((e) => ({ ...e, packageId: undefined }));
            }}
            options={packages.map((p) => ({
              value: p.id,
              label: `${p.name} — ${formatPaise(p.price)}`,
            }))}
          />
          {errors.packageId && <ErrorMsg>{errors.packageId}</ErrorMsg>}
        </FullWidth>

        {/* Amount */}
        <div data-testid="candeza-memberships-form-amount-field">
          <Input
            label={`Amount (₹)${selectedPackage ? ` — default ${formatPaise(selectedPackage.price)}` : ''}`}
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            onChange={(e) =>
              setForm((f) => ({ ...f, amount: e.target.value }))
            }
            placeholder="Amount in ₹ (e.g. 29000.00)"
            error={errors.amount}
          />
        </div>

        {/* Currency */}
        <div>
          <Input
            label="Currency"
            value={form.currency}
            onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
            placeholder="INR"
          />
        </div>

        {/* Recurrence preset */}
        <FullWidth data-testid="candeza-memberships-form-recurrence-preset">
          <Dropdown
            label="Recurrence"
            value={form.recurrencePreset}
            onChange={(v) =>
              setForm((f) => ({ ...f, recurrencePreset: v as RecurrencePresetKey }))
            }
            options={PRESET_KEYS.map((p) => ({ value: p.key, label: p.label }))}
          />
        </FullWidth>

        {/* Custom recurrence builder */}
        {form.recurrencePreset === 'custom' && (
          <FullWidth data-testid="candeza-memberships-form-recurrence-custom-builder">
            <RecurrenceRow>
              <div style={{ flex: 1, minWidth: 100 }}>
                <Dropdown
                  label="Unit"
                  value={form.customUnit}
                  onChange={(v) =>
                    setForm((f) => ({ ...f, customUnit: v as RecurrencePattern['unit'] }))
                  }
                  options={[
                    { value: 'day', label: 'Day' },
                    { value: 'week', label: 'Week' },
                    { value: 'month', label: 'Month' },
                    { value: 'year', label: 'Year' },
                  ]}
                />
              </div>
              <div style={{ flex: 1, minWidth: 80 }}>
                <Input
                  label="Every N"
                  type="number"
                  min="1"
                  max="60"
                  value={form.customInterval}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, customInterval: e.target.value }))
                  }
                />
              </div>
              <div style={{ flex: 1, minWidth: 100 }}>
                <Input
                  label="Day of month (1–28)"
                  type="number"
                  min="1"
                  max="28"
                  value={form.customDayOfMonth}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, customDayOfMonth: e.target.value }))
                  }
                  placeholder="Optional"
                />
              </div>
              {form.customUnit === 'year' && (
                <div style={{ flex: 1, minWidth: 100 }}>
                  <Input
                    label="Month of year (1–12)"
                    type="number"
                    min="1"
                    max="12"
                    value={form.customMonthOfYear}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, customMonthOfYear: e.target.value }))
                    }
                    placeholder="Optional"
                  />
                </div>
              )}
            </RecurrenceRow>
            {errors.recurrence && <ErrorMsg>{errors.recurrence}</ErrorMsg>}
          </FullWidth>
        )}

        {/* First invoice date */}
        <FullWidth data-testid="candeza-memberships-form-first-date-field">
          <Input
            label="First invoice date *"
            type="date"
            value={form.firstInvoiceDate}
            onChange={(e) =>
              setForm((f) => ({ ...f, firstInvoiceDate: e.target.value }))
            }
            error={errors.firstInvoiceDate}
          />
        </FullWidth>
      </FormGrid>

      <ModalActions>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          data-testid="candeza-memberships-form-save-btn"
          isLoading={createMut.isPending}
          onClick={() => void handleSubmit()}
        >
          Create membership
        </Button>
      </ModalActions>
    </Modal>
  );
};
